import { useState } from "react";
import { Box, IconButton, Tooltip } from "@mui/material";
import { AppModal } from "global/components/AppModal";
import CustomLoader from "global/components/CustomLoader/CustomLoader";
import notifiers from "global/constants/NotificationConstants";
import strings from "global/constants/StringConstants";
import { isTruthy, openErrorNotification, setFileName } from "helpers/methods";
import { downloadDocument } from "screens/Tickets/TicketServices";
import { doViewTicketAttachment } from "../ViewTicketService";
import {
  attachmentChipSx,
  attachmentDownloadSx,
  attachmentIconSx,
  attachmentNameSx,
  attachmentRowSx,
  commentAttachmentRowSx,
} from "../ViewTicket.styles";
import { DownloadIcon, FileIcon, PaperclipIcon } from "./ViewTicketIcons";

/** Spreadsheets have no inline preview, so they download instead of opening. */
const NON_VIEWABLE_EXTENSIONS = ["csv", "xls", "xlsx"];

const isViewable = (path: string) => {
  const extension = path.split(".").pop()?.toLowerCase() ?? "";
  return !NON_VIEWABLE_EXTENSIONS.includes(extension);
};

interface TicketAttachmentsProps {
  files?: string[];
  /**
   * The description lists its documents behind a file glyph under the
   * ATTACHMENTS label; a comment carries them on a paperclip, spaced off its
   * body. The variant picks both, as the design draws them.
   */
  variant?: "description" | "comment";
}

/**
 * Row of attachment chips. Clicking the name previews the file in a dialog;
 * the trailing arrow downloads it.
 */
const TicketAttachments = ({
  files,
  variant = "description",
}: TicketAttachmentsProps) => {
  const [showDialog, setShowDialog] = useState<boolean>(false);
  const [uploadedDocument, setUploadedDocument] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);

  if (!files?.length) return null;

  const isComment = variant === "comment";
  const Glyph = isComment ? PaperclipIcon : FileIcon;

  const handleViewDocument = async (path: string) => {
    try {
      setIsLoading(true);
      const data = await doViewTicketAttachment({ app: "inkfree", path });
      if (isTruthy(data.message)) {
        const isAllowedFileType = strings.ALLOWED_ATTACHMENT_TYPE.some(
          (type: string) => data.message.indexOf(type) >= 0,
        );
        setUploadedDocument(
          isAllowedFileType
            ? data.message
            : strings.FileUrlPrefix + encodeURIComponent(data.message),
        );
      }
      setIsLoading(false);
      setShowDialog(true);
    } catch (error: any) {
      setIsLoading(false);
      openErrorNotification(
        isTruthy(error.message) ? error.message : notifiers.GENERIC_ERROR,
      );
    }
  };

  const handleDownloadDocument = async (path: string) => {
    try {
      setIsLoading(true);
      const data = await downloadDocument({ path });
      const link = document.createElement("a");
      const url = window.URL.createObjectURL(data);
      link.style.display = "none";
      link.href = url;
      link.download = path.split("/").pop() ?? "attachment";
      link.click();
      window.URL.revokeObjectURL(url);
      link.remove();
      setIsLoading(false);
    } catch (error: any) {
      setIsLoading(false);
      openErrorNotification(
        isTruthy(error.message) ? error.message : notifiers.GENERIC_ERROR,
      );
    }
  };

  const handleClose = () => {
    setShowDialog(false);
    setUploadedDocument("");
  };

  return (
    <>
      <Box sx={isComment ? commentAttachmentRowSx : attachmentRowSx}>
        {files.map((file: string) => {
          const viewable = isViewable(file);
          const name = setFileName(file);

          return (
            <Box key={file} sx={attachmentChipSx(viewable)}>
              <Glyph sx={attachmentIconSx} />
              <Tooltip title={viewable ? `Preview ${name}` : name} arrow describeChild>
                <Box
                  component={viewable ? "button" : "span"}
                  type={viewable ? "button" : undefined}
                  onClick={
                    viewable ? () => handleViewDocument(file) : undefined
                  }
                  sx={attachmentNameSx(viewable)}
                >
                  {name}
                </Box>
              </Tooltip>
              <Tooltip title="Download" arrow>
                <IconButton
                  aria-label={`Download ${name}`}
                  onClick={() => handleDownloadDocument(file)}
                  sx={attachmentDownloadSx}
                >
                  <DownloadIcon sx={attachmentIconSx} />
                </IconButton>
              </Tooltip>
            </Box>
          );
        })}
      </Box>

      <CustomLoader isLoading={isLoading} />
      <AppModal
        open={showDialog}
        onClose={handleClose}
        title="View File"
        tone="neutral"
        // A document preview — the widest step, with its own iframe scroller.
        size="xl"
        scrollBody={false}
        primaryLabel="Close"
        onPrimary={handleClose}
        hideCancel
      >
        <iframe
          title="Attachment preview"
          src={`${uploadedDocument}#toolbar=0&view=fitH`}
          width="100%"
          height="650px"
          frameBorder="0"
          className="responsive-iframe"
          scrolling="yes"
        />
      </AppModal>
    </>
  );
};

export default TicketAttachments;
